/**
 * Depth and normal render pass.
 * Copyright Higharc Inc. 2020
 */
import { assertIsNotUndefined } from "common/utils/assertOfType";
import * as THREE from "three";
import type { HighlightDataTexture } from "../../HighlightDataTexture";
import {
  isolateModeDeclarationsFragment,
  isolateModeDeclarationsVertex,
  isolateModeFragment,
  isolateModeVertex,
} from "../../materials/shaders/entityIdHighlight";
import { safeShaderReplace } from "../../materials/shaderUtils";
import { EffectComposerPass } from "../examples/jsm/postprocessing/EffectComposerPass";
import { FullScreenQuad } from "../examples/jsm/postprocessing/FullScreenQuad";
import { checkWebGLContextLost } from "./checkWebGLContextLost";
import { DepthDownsampleShader } from "./DepthDownsampleShader";
import type { DepthTextureType } from "./DepthTextureType";
import { DepthToViewZShader } from "./DepthToViewZShader";
import { DisplayNormalShader } from "./DisplayNormalShader";
import { normalPackingHelpersGLSL } from "./normalPackingHelpersGLSL";
import { SSFXRendererOutput } from "./SSFXRendererOutput";

const oldClearColor: THREE.Color = new THREE.Color();
const tempLayers: THREE.Layers = new THREE.Layers();

function saveCameraParams(camera: THREE.Camera) {
  if ((camera as THREE.PerspectiveCamera).isPerspectiveCamera) {
    return { fov: (camera as THREE.PerspectiveCamera).fov, aspect: (camera as THREE.PerspectiveCamera).aspect };
  } else if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
    return {
      left: (camera as THREE.OrthographicCamera).left,
      right: (camera as THREE.OrthographicCamera).right,
      top: (camera as THREE.OrthographicCamera).top,
      bottom: (camera as THREE.OrthographicCamera).bottom,
    };
  }
  return undefined;
}

function restoreCameraParams(camera: THREE.Camera, cameraParams) {
  if ((camera as THREE.PerspectiveCamera).isPerspectiveCamera) {
    const cameraPerspective = camera as THREE.PerspectiveCamera;
    cameraPerspective.fov = cameraParams.fov;
    cameraPerspective.aspect = cameraParams.aspect;
    cameraPerspective.updateProjectionMatrix();
  } else if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
    const cameraOrtho = camera as THREE.OrthographicCamera;
    cameraOrtho.left = cameraParams.left;
    cameraOrtho.right = cameraParams.right;
    cameraOrtho.top = cameraParams.top;
    cameraOrtho.bottom = cameraParams.bottom;
    cameraOrtho.updateProjectionMatrix();
  }
}

function padCamera(camera: THREE.Camera, paddedSize: THREE.Vector2, unpaddedSize: THREE.Vector2): THREE.Vector2 {
  if ((camera as THREE.PerspectiveCamera).isPerspectiveCamera) {
    const cameraPerspective = camera as THREE.PerspectiveCamera;
    // TODO: In case the amount of added padding is not divisible by 2, then we should pad the camera frustum more in one direction.
    // Problem is we're already using setViewOffset for AA.
    const d = unpaddedSize.y / Math.tan(THREE.MathUtils.degToRad(cameraPerspective.fov) * 0.5);
    cameraPerspective.fov = THREE.MathUtils.radToDeg(2.0 * Math.atan(paddedSize.y / d));

    const paddedAspect = paddedSize.x / paddedSize.y;
    const unpaddedAspect = unpaddedSize.x / unpaddedSize.y;
    cameraPerspective.aspect *= paddedAspect / unpaddedAspect;

    cameraPerspective.updateProjectionMatrix();
    return new THREE.Vector2((paddedSize.x - unpaddedSize.x) * 0.5, (paddedSize.y - unpaddedSize.y) * 0.5);
  } else if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
    const cameraOrtho = camera as THREE.OrthographicCamera;
    const width = cameraOrtho.right - cameraOrtho.left;
    const height = cameraOrtho.top - cameraOrtho.bottom;

    const widthIncreasePixels = paddedSize.x - unpaddedSize.x;
    const leftIncreasePixels = Math.floor(widthIncreasePixels * 0.5);
    const pixelWidth = width / unpaddedSize.x;
    cameraOrtho.left -= leftIncreasePixels * pixelWidth;
    cameraOrtho.right += (widthIncreasePixels - leftIncreasePixels) * pixelWidth;

    const heightIncreasePixels = paddedSize.y - unpaddedSize.y;
    const bottomIncreasePixels = Math.floor(heightIncreasePixels * 0.5);
    const pixelHeight = height / unpaddedSize.y;
    cameraOrtho.bottom -= bottomIncreasePixels * pixelHeight;
    cameraOrtho.top += (heightIncreasePixels - bottomIncreasePixels) * pixelHeight;

    cameraOrtho.updateProjectionMatrix();
    return new THREE.Vector2(leftIncreasePixels, bottomIncreasePixels);
  }
  return new THREE.Vector2(0, 0);
}

export interface DepthNormalPassParams {
  renderNormalTexture: boolean;
  enableViewZConversion: boolean;
  downsampleDepthSteps: number;
  normalTextureType: THREE.TextureDataType;

  outputEnabled: boolean;
  output: number;

  depthNormalLayerMask: THREE.Layers;
}

const defaultDepthNormalLayerMask = new THREE.Layers();
defaultDepthNormalLayerMask.enableAll();

export const defaultDepthNormalPassParams: DepthNormalPassParams = {
  renderNormalTexture: true,
  enableViewZConversion: false, // Note that this parameter is set at initialization depending on client device capabilities.
  downsampleDepthSteps: 0,
  normalTextureType: THREE.UnsignedByteType,

  outputEnabled: false,
  output: SSFXRendererOutput.DEPTH,

  depthNormalLayerMask: defaultDepthNormalLayerMask,
};

export class DepthNormalPass extends EffectComposerPass {
  readonly params: DepthNormalPassParams;

  scene: THREE.Scene | undefined;
  camera: THREE.Camera;

  private sizeGranularity: number;
  private unpaddedSize: THREE.Vector2;
  paddedSize: THREE.Vector2;
  paddedPixelOffset: THREE.Vector2;
  paddedProjectionMatrix: THREE.Matrix4;

  private depthTextureTypeInternal: DepthTextureType;
  get depthTextureType(): DepthTextureType {
    return this.depthTextureTypeInternal;
  }
  set depthTextureType(value: DepthTextureType) {
    if (value === this.depthTextureTypeInternal) {
      return;
    }
    this.depthTextureTypeInternal = value;
    this.refreshDepthAndNormalRenderTargets();
  }
  private normalTextureType: THREE.TextureDataType = defaultDepthNormalPassParams.normalTextureType;

  private downsampleDepthSteps: number = defaultDepthNormalPassParams.downsampleDepthSteps;

  private depthMipRenderTargets: THREE.WebGLRenderTarget[];

  private normalMaterial: THREE.MeshNormalMaterial;
  private depthOnlyMaterial: THREE.MeshBasicMaterial;
  private packedDepthMaterial: THREE.MeshDepthMaterial;
  private displayNormalMaterial: THREE.ShaderMaterial;
  private rgbaDepthToViewZMaterial: THREE.ShaderMaterial;
  private depthTextureToViewZMaterial: THREE.ShaderMaterial;
  private depthTextureDownsampleMaterial: THREE.ShaderMaterial;
  private depthRGBATextureDownsampleMaterial: THREE.ShaderMaterial;

  private highlightedIdsUniform: THREE.IUniform = { value: null };
  private isolateModeUniform: THREE.IUniform = { value: false };

  private fsQuad: FullScreenQuad;

  // Render targets are lazily initialized, so that we'll avoid unnecessary allocations. Calling setSize() on a render target will allocate memory even if it is otherwise not used in rendering.
  private depthTextureInternal: THREE.DepthTexture | undefined;
  private depthRenderTarget: THREE.WebGLRenderTarget | undefined;
  private normalRenderTarget: THREE.WebGLRenderTarget | undefined;
  private viewZRenderTarget: THREE.WebGLRenderTarget | undefined;

  storePrevViewZRenderTarget: boolean = true;
  private prevViewZRenderTarget: THREE.WebGLRenderTarget | undefined;

  sizeMultiplier: number; // Can be used to lower the resolution of the render targets.

  constructor(
    scene: THREE.Scene | undefined,
    camera: THREE.Camera,
    depthTextureType: DepthTextureType,
    size?: THREE.Vector2,
    sizeGranularity?: number,
    sizeMultiplier?: number,
  ) {
    super();

    this.scene = scene;
    this.camera = camera;

    this.sizeGranularity = sizeGranularity ?? 1;
    this.sizeMultiplier = sizeMultiplier ?? 1;
    size = size !== undefined ? new THREE.Vector2(size.x, size.y) : new THREE.Vector2(256, 256);
    this.unpaddedSize = size;
    this.paddedSize = this.computePaddedSize(size.x, size.y);
    this.paddedPixelOffset = new THREE.Vector2(0, 0);

    this.paddedProjectionMatrix = new THREE.Matrix4();

    this.depthTextureTypeInternal = depthTextureType ?? 0;

    const scope = this; // eslint-disable-line @typescript-eslint/no-this-alias

    this.params = {
      ...defaultDepthNormalPassParams,

      set downsampleDepthSteps(value) {
        scope.downsampleDepthSteps = value;
        scope.allocateDownsampledDepthTargets();
      },
      get downsampleDepthSteps() {
        return scope.downsampleDepthSteps;
      },

      set normalTextureType(value) {
        if (value === scope.normalTextureType) {
          return;
        }
        scope.normalTextureType = value;
        scope.refreshDepthAndNormalRenderTargets();
      },
      get normalTextureType() {
        return scope.normalTextureType;
      },
    };

    // This array contains the mip levels of the depth texture. Because of WebGL limitations (npot and framebuffer attachment), we store each mip level as a separate texture.
    this.depthMipRenderTargets = [];

    this.normalMaterial = new THREE.MeshNormalMaterial({ name: "DepthNormalPass normalMaterial" });
    this.normalMaterial.defines = { ISOLATE_MODE: 1 };
    this.normalMaterial.onBeforeCompile = (shader) => {
      shader.uniforms.highlightedIds = this.highlightedIdsUniform;
      shader.uniforms.isolateMode = this.isolateModeUniform;

      // Support isolate mode in the normal material
      shader.vertexShader = safeShaderReplace(
        shader.vertexShader,
        "#include <common>",
        `#include <common>
          ${isolateModeDeclarationsVertex}
          `,
        "normalMaterial vertex shader (isolate mode declarations)",
      );
      shader.vertexShader = safeShaderReplace(
        shader.vertexShader,
        "#include <begin_vertex>",
        `#include <begin_vertex>
          ${isolateModeVertex}
          `,
        "normalMaterial vertex shader (isolate mode)",
      );
      shader.fragmentShader = safeShaderReplace(
        shader.fragmentShader,
        "#include <clipping_planes_pars_fragment>",
        `#include <clipping_planes_pars_fragment>
          ${isolateModeDeclarationsFragment}
          `,
        "normalMaterial fragment shader (isolate mode declarations)",
      );

      shader.fragmentShader = safeShaderReplace(
        shader.fragmentShader,
        "#include <clipping_planes_fragment>",
        `#include <clipping_planes_fragment>
          ${isolateModeFragment}
        `,
        "normalMaterial fragment shader (isolate mode logic)",
      );

      // Define gl_PointSize when rendering normals.
      // Note that this may have a slight adverse effect when this shader is used to render triangles or lines.
      // Ideally we'll remove this if the WebGL spec changes to define this behavior differently. See: https://github.com/KhronosGroup/WebGL/pull/3370
      shader.vertexShader = safeShaderReplace(
        shader.vertexShader,
        "#include <project_vertex>",
        `#include <project_vertex>
        gl_PointSize = 1.0;
        `,
        "normalMaterial vertex shader (point size)",
      );

      // Use custom optimized packing for normal data.
      shader.fragmentShader = safeShaderReplace(
        shader.fragmentShader,
        "#include <clipping_planes_pars_fragment>",
        `#include <clipping_planes_pars_fragment>
        ${normalPackingHelpersGLSL}
          `,
        "normalMaterial fragment shader (normal packing)",
      );
      shader.fragmentShader = safeShaderReplace(
        shader.fragmentShader,
        "gl_FragColor = vec4( normalize( normal ) * 0.5 + 0.5, diffuseColor.a )",
        `gl_FragColor = packNormalToRGBA( normal )`,
        "normalMaterial fragment shader (output)",
      );
    };
    this.normalMaterial.blending = THREE.NoBlending;
    this.normalMaterial.userData.disableGrid = true;

    this.depthOnlyMaterial = new THREE.MeshBasicMaterial();
    this.depthOnlyMaterial.colorWrite = false;
    this.depthOnlyMaterial.userData.disableGrid = true;

    this.packedDepthMaterial = new THREE.MeshDepthMaterial({ name: "DepthNormalPass packedDepthMaterial" });
    this.packedDepthMaterial.depthPacking = THREE.RGBADepthPacking;
    this.packedDepthMaterial.blending = THREE.NoBlending;
    this.packedDepthMaterial.userData.disableGrid = true;

    this.displayNormalMaterial = new THREE.ShaderMaterial({
      defines: Object.assign({}, DisplayNormalShader.defines),
      uniforms: THREE.UniformsUtils.clone(DisplayNormalShader.uniforms),
      vertexShader: DisplayNormalShader.vertexShader,
      fragmentShader: DisplayNormalShader.fragmentShader,
      blending: THREE.NoBlending,
      name: "DepthNormalPass displayNormalMaterial",
    });

    this.rgbaDepthToViewZMaterial = new THREE.ShaderMaterial({
      defines: { DEPTH_PACKING: 1, SCALE_DEPTH_VALUES: 0 },
      uniforms: THREE.UniformsUtils.clone(DepthToViewZShader.uniforms),
      vertexShader: DepthToViewZShader.vertexShader,
      fragmentShader: DepthToViewZShader.fragmentShader,
      blending: THREE.NoBlending,
      depthTest: false,
      depthWrite: false,
      name: "DepthNormalPass rgbaDepthToViewZMaterial",
    });
    this.depthTextureToViewZMaterial = new THREE.ShaderMaterial({
      defines: { DEPTH_PACKING: 0, SCALE_DEPTH_VALUES: 0 },
      uniforms: THREE.UniformsUtils.clone(DepthToViewZShader.uniforms),
      vertexShader: DepthToViewZShader.vertexShader,
      fragmentShader: DepthToViewZShader.fragmentShader,
      blending: THREE.NoBlending,
      depthTest: false,
      depthWrite: false,
      name: "DepthNormalPass depthTextureToViewZMaterial",
    });

    this.depthTextureDownsampleMaterial = new THREE.ShaderMaterial({
      defines: { DEPTH_PACKING: 0, MIN_FILTER: 0 },
      uniforms: THREE.UniformsUtils.clone(DepthDownsampleShader.uniforms),
      vertexShader: DepthDownsampleShader.vertexShader,
      fragmentShader: DepthDownsampleShader.fragmentShader,
      blending: THREE.NoBlending,
      name: "DepthNormalPass depthTextureDownsampleMaterial",
    });
    this.depthRGBATextureDownsampleMaterial = new THREE.ShaderMaterial({
      defines: { DEPTH_PACKING: 1, MIN_FILTER: 0 },
      uniforms: THREE.UniformsUtils.clone(DepthDownsampleShader.uniforms),
      vertexShader: DepthDownsampleShader.vertexShader,
      fragmentShader: DepthDownsampleShader.fragmentShader,
      blending: THREE.NoBlending,
      name: "DepthNormalPass depthRGBATextureDownsampleMaterial",
    });

    this.fsQuad = new FullScreenQuad();
  }

  setHighlightDataTexture(highlightDataTexture?: HighlightDataTexture) {
    this.highlightedIdsUniform.value = highlightDataTexture?.texture ?? null;
    this.isolateModeUniform.value = highlightDataTexture?.isolateMode ?? false;
  }

  render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget | null) {
    const oldAutoClear = renderer.autoClear;
    renderer.getClearColor(oldClearColor);
    const oldClearAlpha = renderer.getClearAlpha();

    renderer.autoClear = false;

    this.renderDepthAndNormals(renderer);

    this.renderOutput(renderer, writeBuffer);

    renderer.autoClear = oldAutoClear;
    renderer.setClearColor(oldClearColor, oldClearAlpha);

    this.onAfterPass();
  }

  private renderDepthAndNormals(renderer: THREE.WebGLRenderer) {
    const useDepthTexture = this.depthTextureType !== 0;
    // If we're using depth textures and we render normals, then depth texture is rendered as part of the normal rendering.
    const depthIsRenderedInNormalPass = this.params.renderNormalTexture && useDepthTexture;

    const originalCameraParams = saveCameraParams(this.camera);
    this.paddedPixelOffset = padCamera(
      this.camera,
      new THREE.Vector2(this.paddedSize.x / this.sizeMultiplier, this.paddedSize.y / this.sizeMultiplier),
      this.unpaddedSize,
    );
    this.paddedProjectionMatrix.copy(this.camera.projectionMatrix);

    if (!depthIsRenderedInNormalPass) {
      // Clear rule : far clipping plane in both RGBA and Basic encoding
      if (useDepthTexture) {
        this.renderOverride(renderer, this.depthOnlyMaterial, this.lazyDepthRenderTarget(), 0x000000, 1.0);
      } else {
        this.renderOverride(renderer, this.packedDepthMaterial, this.lazyDepthRenderTarget(), 0x000000, 1.0);
      }
    }

    if (this.params.renderNormalTexture) {
      // Clear rule : default normal is facing the camera
      this.renderOverride(renderer, this.normalMaterial, this.lazyNormalRenderTarget(), 0x7777ff, 1.0);
    }

    if (this.params.enableViewZConversion) {
      if (this.storePrevViewZRenderTarget) {
        const temp = this.prevViewZRenderTarget;
        this.prevViewZRenderTarget = this.viewZRenderTarget;
        this.viewZRenderTarget = temp;
      }
      if (this.viewZRenderTarget === undefined) {
        this.viewZRenderTarget = new THREE.WebGLRenderTarget(this.paddedSize.x, this.paddedSize.y, {
          minFilter: THREE.NearestFilter,
          magFilter: THREE.NearestFilter,
          format: THREE.RedFormat,
          type: THREE.FloatType,
          depthBuffer: false,
          stencilBuffer: false,
        });
      }
      const depthTexture = useDepthTexture ? this.depthTextureInternal : this.lazyDepthRenderTarget().texture;
      if (depthTexture !== undefined) {
        this.depthToViewZPass(renderer, depthTexture, !useDepthTexture, false, this.viewZRenderTarget);
      }
    }

    this.downsampleDepth(renderer, useDepthTexture);

    restoreCameraParams(this.camera, originalCameraParams);
  }

  private depthToViewZPass(
    renderer: THREE.WebGLRenderer,
    sourceTexture: THREE.Texture,
    isSourceRGBAPacked: boolean,
    normalize: boolean,
    writeBuffer: THREE.WebGLRenderTarget | null,
  ) {
    if (isSourceRGBAPacked) {
      this.rgbaDepthToViewZMaterial.uniforms["tDepth"].value = sourceTexture;
      this.rgbaDepthToViewZMaterial.uniforms["cameraNear"].value = (this.camera as THREE.PerspectiveCamera).near;
      this.rgbaDepthToViewZMaterial.uniforms["cameraFar"].value = (this.camera as THREE.PerspectiveCamera).far;
      this.rgbaDepthToViewZMaterial.uniforms["perspectiveCamera"].value = (
        this.camera as THREE.PerspectiveCamera
      ).isPerspectiveCamera;
      this.rgbaDepthToViewZMaterial.uniforms["normalizeViewZ"].value = normalize;
      this.renderPass(renderer, this.rgbaDepthToViewZMaterial, writeBuffer);
    } else {
      this.depthTextureToViewZMaterial.uniforms["tDepth"].value = sourceTexture;
      this.depthTextureToViewZMaterial.uniforms["cameraNear"].value = (this.camera as THREE.PerspectiveCamera).near;
      this.depthTextureToViewZMaterial.uniforms["cameraFar"].value = (this.camera as THREE.PerspectiveCamera).far;
      this.depthTextureToViewZMaterial.uniforms["perspectiveCamera"].value = (
        this.camera as THREE.PerspectiveCamera
      ).isPerspectiveCamera;
      this.depthTextureToViewZMaterial.uniforms["normalizeViewZ"].value = normalize;
      this.renderPass(renderer, this.depthTextureToViewZMaterial, writeBuffer);
    }
  }

  private downsampleDepth(renderer: THREE.WebGLRenderer, useDepthTexture: boolean) {
    let depthSrcSizeX = this.paddedSize.x;
    let depthSrcSizeY = this.paddedSize.y;
    for (let downsampleStep = 0; downsampleStep < this.params.downsampleDepthSteps; ++downsampleStep) {
      const depthDstSizeX = Math.ceil(depthSrcSizeX * 0.5);
      const depthDstSizeY = Math.ceil(depthSrcSizeY * 0.5);
      if (downsampleStep === 0) {
        if (useDepthTexture) {
          this.depthTextureDownsampleMaterial.uniforms["tDepth"].value = this.depthTextureInternal;
          this.depthTextureDownsampleMaterial.uniforms["invSize"].value.set(
            1.0 / depthSrcSizeX,
            1.0 / this.paddedSize.y,
          );
          this.renderPass(
            renderer,
            this.depthTextureDownsampleMaterial,
            this.depthMipRenderTargets[downsampleStep],
            0x000000,
            1.0,
          );
        } else {
          this.depthRGBATextureDownsampleMaterial.uniforms["tDepth"].value = this.lazyDepthRenderTarget().texture;
          this.depthRGBATextureDownsampleMaterial.uniforms["invSize"].value.set(
            1.0 / depthSrcSizeX,
            1.0 / depthSrcSizeY,
          );
          this.renderPass(
            renderer,
            this.depthRGBATextureDownsampleMaterial,
            this.depthMipRenderTargets[downsampleStep],
            0x000000,
            1.0,
          );
        }
      } else {
        this.depthRGBATextureDownsampleMaterial.uniforms["tDepth"].value =
          this.depthMipRenderTargets[downsampleStep - 1].texture;
        this.depthRGBATextureDownsampleMaterial.uniforms["invSize"].value.set(1.0 / depthSrcSizeX, 1.0 / depthSrcSizeY);
        this.renderPass(
          renderer,
          this.depthRGBATextureDownsampleMaterial,
          this.depthMipRenderTargets[downsampleStep],
          0x000000,
          1.0,
        );
      }
      depthSrcSizeX = depthDstSizeX;
      depthSrcSizeY = depthDstSizeY;
    }
  }

  private renderOutput(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget | null) {
    if (!this.params.outputEnabled) {
      return;
    }

    const requestedDepthMip =
      this.params.output >= SSFXRendererOutput.DEPTH_MIP_1 && this.params.output <= SSFXRendererOutput.DEPTH_MIP_10;

    if (requestedDepthMip && this.depthMipRenderTargets.length > 0) {
      const mipIndex = Math.min(
        this.params.output - SSFXRendererOutput.DEPTH_MIP_1,
        this.depthMipRenderTargets.length - 1,
      );
      this.depthToViewZPass(renderer, this.depthMipRenderTargets[mipIndex].texture, true, true, writeBuffer);
      return;
    }

    if (this.params.output === SSFXRendererOutput.DEPTH || requestedDepthMip) {
      const useDepthTexture = this.depthTextureType !== 0;
      if (useDepthTexture) {
        if (this.depthTextureInternal !== undefined) {
          this.depthToViewZPass(renderer, this.depthTextureInternal, false, true, writeBuffer);
        }
      } else {
        this.depthToViewZPass(renderer, this.lazyDepthRenderTarget().texture, true, true, writeBuffer);
      }
      return;
    }

    if (this.params.output === SSFXRendererOutput.NORMAL && this.params.renderNormalTexture) {
      this.displayNormalMaterial.uniforms["tNormal"].value = this.lazyNormalRenderTarget().texture;
      this.renderPass(renderer, this.displayNormalMaterial, writeBuffer);
      return;
    }
  }

  private refreshDepthAndNormalRenderTargets() {
    // We need to recreate the render targets even if we're just changing the depth texture type.
    // Three.js doesn't seem to be able to handle changing WebGLRenderTarget.depthTexture multiple times.
    if (this.normalRenderTarget !== undefined) {
      this.normalRenderTarget?.dispose();
      this.normalRenderTarget = undefined;
      // The normal render target will be recreated when lazyNormalRenderTarget() is next called.
    }

    if (this.depthRenderTarget !== undefined) {
      this.depthRenderTarget?.dispose();
      this.depthRenderTarget = undefined;
      // The depth render target will be recreated when lazyDepthRenderTarget() is next called.
    }
    if (this.depthTextureInternal !== undefined) {
      this.depthTextureInternal.dispose();
      this.depthTextureInternal = undefined;
    }
  }

  private allocateDownsampledDepthTargets() {
    let depthSizeX = this.paddedSize.x;
    let depthSizeY = this.paddedSize.y;
    for (let i = 0; i < this.params.downsampleDepthSteps; ++i) {
      depthSizeX = Math.ceil(depthSizeX * 0.5);
      depthSizeY = Math.ceil(depthSizeY * 0.5);
      if (i >= this.depthMipRenderTargets.length) {
        this.depthMipRenderTargets.push(
          new THREE.WebGLRenderTarget(depthSizeX, depthSizeY, {
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            format: THREE.RGBAFormat,
            depthBuffer: false,
            stencilBuffer: false,
          }),
        );
      } else {
        this.depthMipRenderTargets[i].setSize(depthSizeX, depthSizeY);
      }
    }
  }

  isDepthRGBAPacked() {
    return this.depthTextureType === 0;
  }

  private lazyDepthTexture() {
    if (this.depthTextureType === 0) {
      return undefined;
    }
    if (this.depthTextureInternal === undefined || this.depthTextureInternal.type !== this.depthTextureType) {
      this.depthTextureInternal = new THREE.DepthTexture(this.paddedSize.x, this.paddedSize.y);
      this.depthTextureInternal.type = this.depthTextureType;
      this.depthTextureInternal.minFilter = THREE.NearestFilter;
      this.depthTextureInternal.magFilter = THREE.NearestFilter;
    }
    return this.depthTextureInternal;
  }

  private lazyDepthRenderTarget() {
    if (this.depthRenderTarget === undefined) {
      const depthTexture = this.depthTextureType !== 0 ? this.lazyDepthTexture() : undefined;
      this.depthRenderTarget = new THREE.WebGLRenderTarget(this.paddedSize.x, this.paddedSize.y, {
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        format: THREE.RGBAFormat,
        depthTexture,
      });
    }
    return this.depthRenderTarget;
  }

  private lazyNormalRenderTarget() {
    if (this.normalRenderTarget === undefined) {
      const depthTexture = this.depthTextureType !== 0 ? this.lazyDepthTexture() : undefined;
      this.normalRenderTarget = new THREE.WebGLRenderTarget(this.paddedSize.x, this.paddedSize.y, {
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        format: THREE.RGBAFormat,
        depthTexture,
      });
    }
    return this.normalRenderTarget;
  }

  get depthTexture() {
    const useDepthTexture = this.depthTextureType !== 0;
    return useDepthTexture ? this.depthTextureInternal : this.lazyDepthRenderTarget().texture;
  }

  get viewZTexture() {
    return this.viewZRenderTarget?.texture ?? null;
  }

  get normalTexture() {
    return this.lazyNormalRenderTarget().texture;
  }

  getDepthMip(downsampledDepthIndex: number) {
    return this.depthMipRenderTargets[downsampledDepthIndex].texture;
  }

  get prevViewZTexture() {
    return this.prevViewZRenderTarget?.texture ?? null;
  }

  private renderPass(
    renderer: THREE.WebGLRenderer,
    passMaterial: THREE.Material,
    renderTarget: THREE.WebGLRenderTarget | null,
    clearColor?: THREE.ColorRepresentation,
    clearAlpha?: number,
  ) {
    renderer.setRenderTarget(renderTarget);

    const clearNeeded = clearColor !== undefined && clearColor !== null;
    if (clearNeeded) {
      renderer.setClearColor(clearColor);
      renderer.setClearAlpha(clearAlpha || 0.0);
      renderer.clear();
    }

    this.fsQuad.material = passMaterial;
    this.fsQuad.render(renderer);
  }

  private renderOverride(
    renderer: THREE.WebGLRenderer,
    overrideMaterial: THREE.Material,
    renderTarget: THREE.WebGLRenderTarget,
    clearColor: THREE.ColorRepresentation,
    clearAlpha: number,
  ) {
    tempLayers.mask = this.camera.layers.mask;
    this.camera.layers.mask &= this.params.depthNormalLayerMask.mask;

    assertIsNotUndefined(this.scene);

    try {
      renderer.setRenderTarget(renderTarget);

      renderer.setClearColor(clearColor);
      renderer.setClearAlpha(clearAlpha);
      renderer.clear();

      this.scene.overrideMaterial = overrideMaterial;

      renderer.render(this.scene, this.camera);
    } catch (e) {
      checkWebGLContextLost(renderer, e, "Depth/normal rendering failed.");
    }
    this.scene.overrideMaterial = null;

    this.camera.layers.mask = tempLayers.mask;
  }

  private computePaddedSize(width: number, height: number) {
    width = Math.ceil((width * this.sizeMultiplier) / this.sizeGranularity) * this.sizeGranularity;
    height = Math.ceil((height * this.sizeMultiplier) / this.sizeGranularity) * this.sizeGranularity;
    return new THREE.Vector2(width, height);
  }

  setSizeGranularity(sizeGranularity: number) {
    if (this.sizeGranularity === sizeGranularity) {
      return;
    }
    this.sizeGranularity = sizeGranularity;
    this.refreshSize();
  }

  setSizeMultiplier(sizeMultiplier: number) {
    if (this.sizeMultiplier === sizeMultiplier) {
      return;
    }
    this.sizeMultiplier = sizeMultiplier;
    this.refreshSize();
  }

  setSize(width: number, height: number) {
    super.setSize(width, height);
    this.unpaddedSize = new THREE.Vector2(width, height);
    this.refreshSize();
  }

  private refreshSize() {
    this.paddedSize = this.computePaddedSize(this.unpaddedSize.x, this.unpaddedSize.y);
    this.normalRenderTarget?.setSize(this.paddedSize.x, this.paddedSize.y);
    this.depthRenderTarget?.setSize(this.paddedSize.x, this.paddedSize.y);
    this.viewZRenderTarget?.setSize(this.paddedSize.x, this.paddedSize.y);
    this.prevViewZRenderTarget?.setSize(this.paddedSize.x, this.paddedSize.y);
    this.allocateDownsampledDepthTargets();
  }

  dispose() {
    this.depthTextureInternal?.dispose();
    this.normalRenderTarget?.dispose();
    this.depthRenderTarget?.dispose();
    this.viewZRenderTarget?.dispose();
    this.prevViewZRenderTarget?.dispose();

    for (let i = 0; i < this.depthMipRenderTargets.length; ++i) {
      this.depthMipRenderTargets[i].dispose();
    }

    this.depthOnlyMaterial.dispose();
    this.packedDepthMaterial.dispose();
    this.normalMaterial.dispose();
    this.displayNormalMaterial.dispose();
    this.rgbaDepthToViewZMaterial.dispose();
    this.depthTextureToViewZMaterial.dispose();
  }

  onAfterPass() {}
}
